import EntityService from '@/services/EntityService'
import { FirestoreDataConverter, collection, doc, setDoc, query, getDocs, limit, orderBy, collectionGroup, deleteDoc } from 'firebase/firestore'
import FirebaseService from './FirebaseService'

export default class SubCollectionEntityService<T> extends EntityService<T>{
  protected parentDocument: string
  protected useCollectionGroup: boolean = false

  constructor({ parentDocument, subCollectionName, converter }:
    { parentDocument: string, subCollectionName: string, converter: FirestoreDataConverter<T> }
  ) {
    super({ collectionName: subCollectionName, converter })
    this.parentDocument = parentDocument
    if (parentDocument === '') {
      this.useCollectionGroup = true
    }
  }

  /**
   * Create a new record
   *
   * @param data
   * @returns Promise<T>
   */
  async create(data: T) {
    const db = await FirebaseService.firestore()
    const record = doc(db, this.parentDocument, this.collectionName).withConverter(this.converter)
    const docData = Object.assign(data, {
      id: record.id,
      deleted: false,
    })

    await setDoc(record, docData, { merge: true })

    return Object.assign(data, { id: record.id }) as T
  }

   /**
     * Find all records in the collection
     *
     * @returns Promise<T[]>
     */
  public async all() {
    if (this.useCollectionGroup) return super.all()

    const db = await FirebaseService.firestore()
    const queryRef = query(collection(db, `${this.parentDocument}/${this.collectionName}`)).withConverter(this.converter)
    const records = (await getDocs(queryRef)).docs

    return records.map(doc => doc.data()) as T[]
  }

   /**
   * Get records from the collection
   *
   * @param count
   * @returns Promise<T[]>
   */
   public async take(count: number) {
    const db = await FirebaseService.firestore()
    const collectionRef = this.useCollectionGroup ? collectionGroup(db, this.collectionName) : collection(db, `${this.parentDocument}/${this.collectionName}`)
    const querySnapshot = query(collectionRef, limit(count)).withConverter(this.converter)

    return (await getDocs(querySnapshot)).docs.map(doc => doc.data()) as T[]
   }

  /**
   * Get the first record from the collection
   *
   * @param count
   * @returns Promise<T>
   */
  public async first(count: number = 1) {
    const db = await FirebaseService.firestore()
    const collectionRef = this.useCollectionGroup ? collectionGroup(db, this.collectionName) : collection(db, `${this.parentDocument}/${this.collectionName}`)
    const querySnapshot = query(collectionRef, orderBy('createdAt', 'desc'), limit(count)).withConverter(this.converter)

    return (await getDocs(querySnapshot)).docs.map(doc => doc.data())[0] as T
  }

  /**
   * Delete all records in the sub-collection
   *
   * @returns Promise<boolean>
   */

  public async deleteAll(): Promise<boolean> {
    const db = await FirebaseService.firestore()
    const collectionRef = collection(db, `${this.parentDocument}/${this.collectionName}`)
    const querySnapshot = await getDocs(collectionRef)

    const deletePromises = querySnapshot.docs.map(doc => {
      return deleteDoc(doc.ref)
    })

    await Promise.all(deletePromises)
    return true
  }

  public async update(id: string, data: Partial<T>): Promise<T> {
    const db = await FirebaseService.firestore()
    const docRef = doc(db, `${this.parentDocument}/${this.collectionName}`, id).withConverter(this.converter)

    await setDoc(docRef, data, { merge: true })

    return Object.assign(data, { id }) as T
  }

}
